import React from 'react';

const RadarScan = ({ p5, width, height, pulseAngle, x, y, currentState }) => {
  const centerX = x || width/2;
  const centerY = y || height/2;
  
  // Grid parameters - significantly reduced size
  const hexSize = 8; // Reduced from 15
  const hexHeight = hexSize * Math.sqrt(3);
  const hexWidth = hexSize * 2;
  const cols = Math.ceil(width / (hexWidth * 0.75)) + 2;
  const rows = Math.ceil(height / hexHeight) + 2;

  // Clear with fade effect
  p5.fill(0, 0, 0, 20);
  p5.noStroke();
  p5.rect(0, 0, width, height);

  if (currentState === 'SCANNING') {
    p5.blendMode(p5.ADD);
    
    // Draw very subtle hexagonal grid
    for (let row = -1; row < rows; row++) {
      for (let col = -1; col < cols; col++) {
        const xOffset = col * hexWidth * 0.75;
        const yOffset = row * hexHeight + (col % 2) * (hexHeight / 2);
        const x = xOffset;
        const y = yOffset;

        // Calculate distance from center for energy pulse
        const dx = x - centerX;
        const dy = y - centerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Create subtle pulsing effect
        const timePulse = p5.sin(p5.frameCount * 0.05 - dist * 0.02);
        const energyLevel = p5.map(timePulse, -1, 1, 0, 1);
        
        // Only draw if within certain distance from center
        if (dist < Math.min(width, height) * 0.4) {
          p5.push();
          p5.translate(x, y);

          // Very subtle alpha for grid
          const alpha = p5.map(energyLevel, 0, 1, 5, 40); // Significantly reduced from 15, 100
          p5.stroke(0, 255, 0, alpha);
          p5.noFill();
          
          // Simplified hexagon drawing
          p5.beginShape();
          for (let i = 0; i < 6; i++) {
            const angle = i * Math.PI / 3;
            const hx = hexSize * Math.cos(angle);
            const hy = hexSize * Math.sin(angle);
            p5.vertex(hx, hy);
          }
          p5.endShape(p5.CLOSE);

          // Simplified energy core
          if (energyLevel > 0.8) { // Only show cores at peak energy
            const coreSize = hexSize * 0.1 * energyLevel; // Further reduced
            p5.noStroke();
            p5.fill(0, 255, 0, alpha * 2);
            p5.ellipse(0, 0, coreSize, coreSize);
          }
          
          p5.pop();
        }
      }
    }

    // Keep prominent scanning beam effect
    p5.push();
    p5.translate(centerX, centerY);
    const scanAngle = p5.frameCount * 0.02;
    const scanRadius = Math.min(width, height) * 0.8;
    
    // Draw scanning lines
    for (let i = 0; i < 3; i++) {
      const angle = scanAngle + (i * Math.PI * 2 / 3);
      const x1 = Math.cos(angle) * scanRadius;
      const y1 = Math.sin(angle) * scanRadius;
      
      p5.stroke(0, 255, 0, 50);
      p5.line(0, 0, x1, y1);
      
      // Add geometric patterns along scan lines
      for (let j = 0; j < 5; j++) {
        const t = j / 4;
        const px = x1 * t;
        const py = y1 * t;
        const patternSize = 10 * (1 - t);
        
        p5.noFill();
        p5.stroke(0, 255, 0, 100 * (1 - t));
        p5.rect(px - patternSize/2, py - patternSize/2, patternSize, patternSize);
      }
    }
    p5.pop();

    // Keep prominent geometric scan circles
    const numCircles = 3;
    for (let i = 0; i < numCircles; i++) {
      const progress = ((p5.frameCount * 2 + i * 120) % 300) / 300;
      const circleRadius = progress * Math.min(width, height) * 0.8;
      
      p5.noFill();
      p5.stroke(0, 255, 0, 50 * (1 - progress));
      p5.ellipse(centerX, centerY, circleRadius * 2);
      
      // Add geometric markers on circles
      const numMarkers = 6;
      for (let j = 0; j < numMarkers; j++) {
        const markerAngle = (j * Math.PI * 2 / numMarkers) + (progress * Math.PI * 2);
        const mx = centerX + Math.cos(markerAngle) * circleRadius;
        const my = centerY + Math.sin(markerAngle) * circleRadius;
        
        p5.push();
        p5.translate(mx, my);
        p5.rotate(markerAngle);
        p5.stroke(0, 255, 0, 100 * (1 - progress));
        p5.line(-5, -5, 5, 5);
        p5.line(-5, 5, 5, -5);
        p5.pop();
      }
    }
  }

  // Reset blend mode
  p5.blendMode(p5.BLEND);
};

export default RadarScan;